import { z } from "zod";
import { TenantType, TenantStatus } from "./types";

/**
 * Zod schemas for tenant API validation
 * Matching backend validation from docs/apis/tenants.md
 */

// Helper functions
const trimString = (val: string) => val.trim();
const noSpecialCharsRegex = /^[a-zA-Z0-9\s\-_]+$/;
const tenantNameRegex = /^[^\s].*[^\s]$/;
const accountNameRegex = /^[a-zA-Z0-9\s\-_]+$/;
const salesforceIdRegex = /^[a-zA-Z0-9]{20}$/;
const phoneRegex = /^\d+$/;

/**
 * Parse DD/MM/YYYY date string to Date object
 */
export const parseDDMMYYYY = (dateString: string): Date => {
  const parts = dateString.split("/");
  if (parts.length !== 3) {
    throw new Error("Date must be in DD/MM/YYYY format");
  }
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
  const year = parseInt(parts[2], 10);

  // Validate parsed values are numbers
  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    throw new Error("Date must contain valid numbers");
  }

  // Validate month is in range (0-11)
  if (month < 0 || month > 11) {
    throw new Error("Month must be between 01 and 12");
  }

  // Validate day is in reasonable range
  if (day < 1 || day > 31) {
    throw new Error("Day must be between 01 and 31");
  }

  // Validate year is reasonable
  if (year < 1900 || year > 2100) {
    throw new Error("Year must be between 1900 and 2100");
  }

  // Create date in UTC at midday (12:00:00.000)
  const date = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));

  // Validate the date is valid and matches input
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date: ${dateString}`);
  }

  // Verify the date components match (in UTC)
  const utcDay = date.getUTCDate();
  const utcMonth = date.getUTCMonth();
  const utcYear = date.getUTCFullYear();

  if (utcDay !== day || utcMonth !== month || utcYear !== year) {
    throw new Error(`Invalid date: ${dateString}`);
  }

  return date;
};

/**
 * Customer Admin Schema
 */
export const customerAdminSchema = z.object({
  given_name: z
    .string()
    .min(1, "Given name is required")
    .transform(trimString)
    .pipe(
      z
        .string()
        .min(2, "Given name must be at least 2 characters")
        .max(100, "Given name cannot exceed 100 characters")
        .refine(
          (val) => noSpecialCharsRegex.test(val),
          "Given name cannot contain special characters",
        ),
    ),
  surname: z
    .string()
    .optional()
    .transform((val) => (val ? trimString(val) : undefined))
    .pipe(
      z
        .string()
        .min(2, "Surname must be at least 2 characters")
        .max(100, "Surname cannot exceed 100 characters")
        .refine(
          (val) => noSpecialCharsRegex.test(val),
          "Surname cannot contain special characters",
        )
        .optional(),
    ),
  other_name: z
    .string()
    .optional()
    .transform((val) => (val ? trimString(val) : undefined))
    .pipe(
      z
        .string()
        .min(2, "Other name must be at least 2 characters")
        .max(100, "Other name cannot exceed 100 characters")
        .refine(
          (val) => noSpecialCharsRegex.test(val),
          "Other name cannot contain special characters",
        )
        .optional(),
    ),
  preferred_name: z
    .string()
    .optional()
    .transform((val) => (val ? trimString(val) : undefined))
    .pipe(
      z
        .string()
        .min(2, "Preferred name must be at least 2 characters")
        .max(100, "Preferred name cannot exceed 100 characters")
        .refine(
          (val) => noSpecialCharsRegex.test(val),
          "Preferred name cannot contain special characters",
        )
        .optional(),
    ),
  contact_email: z
    .string()
    .min(1, "Contact email is required")
    .transform((val) => val.trim().toLowerCase())
    .pipe(
      z
        .string()
        .min(1, "Contact email is required")
        .email("Invalid email format"),
    ),
  country: z
    .string()
    .min(1, "Country is required")
    .transform((val) => val.trim().toUpperCase())
    .refine((val) => val.length === 2, "Country code must be 2 characters"),
  phone_code: z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      const trimmed = String(val).trim();
      return phoneRegex.test(trimmed);
    }, "Phone code must be numeric")
    .transform((val) => (val ? String(val).trim() : undefined)),
  phone_number: z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      const trimmed = String(val).trim();
      return phoneRegex.test(trimmed);
    }, "Phone number must be numeric")
    .transform((val) => (val ? String(val).trim() : undefined)),
});

/**
 * Domain schema for validation
 */
export const domainSchema = z
  .string()
  .trim()
  .min(3, "Domain must be at least 3 characters long")
  .max(253, "Domain must be at most 253 characters long")
  .regex(
    /^(?=.{1,253}\.?$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}\.?$/,
    "Invalid domain format",
  );

/**
 * Create Tenant Request Body Schema
 * Note: Domain uniqueness validation is handled separately via useVerifyDomains hook
 */
export const createTenantBodySchema = z
  .object({
    // Step 1: General Information
    tenant_name: z
      .string()
      .min(1, "Tenant name is required")
      .transform(trimString)
      .pipe(
        z
          .string()
          .min(2, "Tenant name must be at least 2 characters")
          .max(100, "Tenant name cannot exceed 100 characters")
          .refine(
            (val) => tenantNameRegex.test(val),
            "Tenant name cannot have leading or trailing spaces",
          ),
      ),
    type: z.nativeEnum(TenantType, {
      errorMap: () => ({ message: "Type must be CUSTOMER or LSP" }),
    }),
    demo_tenant: z.boolean().optional().default(false),
    account_name: z
      .string()
      .min(1, "Account name is required")
      .transform(trimString)
      .pipe(
        z
          .string()
          .min(2, "Account name must be at least 2 characters")
          .max(100, "Account name cannot exceed 100 characters")
          .refine(
            (val) => accountNameRegex.test(val),
            "Account name can only contain letters, digits, spaces, hyphens, and underscores",
          )
          .refine(
            (val) => tenantNameRegex.test(val),
            "Account name cannot have leading or trailing spaces",
          ),
      ),
    domains: z.array(domainSchema).min(1, "At least one domain is required"),
    total_number_of_users: z
      .number()
      .int("Total number of users must be an integer")
      .min(1, "Total number of users must be at least 1")
      .max(9999, "Total number of users cannot exceed 9999")
      .or(
        z
          .string()
          .transform((val) => parseInt(val, 10))
          .pipe(
            z
              .number()
              .int("Total number of users must be an integer")
              .min(1, "Total number of users must be at least 1")
              .max(9999, "Total number of users cannot exceed 9999"),
          ),
      ),
    salesforce_id: z
      .string()
      .optional()
      .refine(
        (val) =>
          !val || val.trim() === "" || salesforceIdRegex.test(val.trim()),
        "Salesforce ID must be exactly 20 alphanumeric characters",
      ),
    country: z
      .string()
      .min(1, "Country is required")
      .transform((val) => val.trim().toUpperCase())
      .refine((val) => val.length === 2, "Country code must be 2 characters"),
    status: z.nativeEnum(TenantStatus, {
      errorMap: () => ({ message: "Status must be ACTIVE or INACTIVE" }),
    }),
    billing_start_date: z
      .string()
      .min(1, "Billing start date is required")
      .regex(
        /^\d{2}\/\d{2}\/\d{4}$/,
        "Billing start date must be in DD/MM/YYYY format",
      )
      .transform((val) => parseDDMMYYYY(val))
      .refine((val) => !isNaN(val.getTime()), "Invalid billing start date"),
    billing_end_date: z
      .union([
        z
          .string()
          .regex(
            /^\d{2}\/\d{2}\/\d{4}$/,
            "Billing end date must be in DD/MM/YYYY format",
          )
          .transform((val) => {
            try {
              const parsed = parseDDMMYYYY(val);
              if (!(parsed instanceof Date) || isNaN(parsed.getTime())) {
                throw new Error("Invalid billing end date");
              }
              return parsed;
            } catch (error) {
              throw new Error(
                error instanceof Error
                  ? error.message
                  : "Invalid billing end date format",
              );
            }
          })
          .refine(
            (val) => val instanceof Date && !isNaN(val.getTime()),
            "Invalid billing end date",
          ),
        z.null(),
        z.undefined(),
      ])
      .optional()
      .nullable(),
    organization_id: z.string().uuid("Organization ID must be a valid UUID"),

    // Step 2: Customer Admin
    customer_admin: customerAdminSchema,

    // Local Team specific fields
    supports_payroll_plus: z.boolean().optional(),
    payslip_portal_link: z
      .string()
      .url("Payslip Portal Link must be a valid URL")
      .optional(),
  })
  .refine(
    (data) => {
      if (data.billing_end_date && data.billing_start_date) {
        return data.billing_end_date > data.billing_start_date;
      }
      return true;
    },
    {
      message: "Billing end date must be after billing start date",
      path: ["billing_end_date"],
    },
  )
  .refine(
    (data) => {
      // If supports_payroll_plus is true, payslip_portal_link must be provided
      if (data.supports_payroll_plus === true) {
        return (
          data.payslip_portal_link !== undefined &&
          data.payslip_portal_link !== null &&
          data.payslip_portal_link.trim() !== ""
        );
      }
      return true;
    },
    {
      message: "Payslip Portal Link is required when Payroll + is supported",
      path: ["payslip_portal_link"],
    },
  );

/**
 * Create Tenant Response Schema
 */
export const createTenantResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    tenant_id: z.string().uuid(),
    customer_admin_id: z.string().uuid(),
  }),
  message: z.string(),
  statusCode: z.literal(201),
  status: z.literal(true),
});

/**
 * Verify Domain Availability Query Schema
 */
export const verifyDomainAvailabilityQuerySchema = z.object({
  domains: z
    .union([
      // Handle comma-separated string
      z.string().transform((val) => {
        if (!val || val.trim() === "") return [];
        return val
          .split(",")
          .map((d) => d.trim())
          .filter((d) => d.length > 0);
      }),
      // Handle array
      z.array(z.string()),
    ])
    .pipe(
      z
        .array(z.string())
        .min(1, "At least one domain is required")
        .max(100, "Maximum 100 domains can be checked at once"),
    )
    .transform((domains) => {
      // Remove duplicates while preserving order
      const seen = new Set<string>();
      return domains.filter((domain) => {
        const normalized = domain.trim().toLowerCase();
        if (seen.has(normalized)) {
          return false;
        }
        seen.add(normalized);
        return true;
      });
    }),
});

/**
 * Verify Domain Availability Response Schema
 */
export const verifyDomainAvailabilityResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    results: z.array(
      z.object({
        domain: z.string(),
        available: z.boolean(),
        tenant_id: z.string().uuid().nullable(),
        tenant_name: z.string().nullable(),
      }),
    ),
  }),
  message: z.string(),
  statusCode: z.literal(200),
  status: z.literal(true),
});
